/// <reference types="node" />
/**
 * Header name for preserved query parameters.
 * The client sends its URL query params as a base64-encoded string in this header.
 */
export const PRESERVED_QUERY_PARAMS_HEADER = 'x-preserved-query-params'

/**
 * Encodes a query string (e.g., "?r=app&foo=bar" or "r=app&foo=bar") to base64.
 * Strips the leading "?" if present.
 *
 * @param queryString - The query string to encode (with or without leading "?")
 * @returns Base64-encoded query string, or undefined if input is empty
 */
export function encodeQueryParams(queryString: string): string | undefined {
  const trimmed = queryString.trim()
  if (!trimmed || trimmed === '?') {
    return undefined
  }

  // Remove leading "?" if present
  const normalized = trimmed.startsWith('?') ? trimmed.slice(1) : trimmed

  if (!normalized) {
    return undefined
  }

  // For browser environments
  if (typeof btoa !== 'undefined') {
    return btoa(normalized)
  }

  // For Node.js environments
  return Buffer.from(normalized, 'utf-8').toString('base64')
}

/**
 * Decodes a base64-encoded query string back to its original form.
 * Does NOT include a leading "?" in the result.
 *
 * @param encodedQueryParams - Base64-encoded query string
 * @returns Decoded query string without leading "?", or undefined if invalid
 */
export function decodeQueryParams(encodedQueryParams: string | undefined): string | undefined {
  if (!encodedQueryParams) {
    return undefined
  }

  try {
    // For browser environments
    if (typeof atob !== 'undefined') {
      return atob(encodedQueryParams)
    }

    // For Node.js environments
    return Buffer.from(encodedQueryParams, 'base64').toString('utf-8')
  } catch (error) {
    // Invalid base64 string
    return undefined
  }
}

/**
 * Appends preserved query params to a URL.
 * Handles URLs that may already have query params.
 *
 * @param baseUrl - The base URL (e.g., "/authlink/123/abc")
 * @param preservedQueryParams - Base64-encoded query params from header
 * @returns URL with appended query params
 */
export function appendPreservedQueryParams(baseUrl: string, preservedQueryParams: string | undefined): string {
  if (!preservedQueryParams) {
    return baseUrl
  }

  const decoded = decodeQueryParams(preservedQueryParams)
  if (!decoded) {
    return baseUrl
  }

  // Check if URL already has query params
  const separator = baseUrl.includes('?') ? '&' : '?'
  return `${baseUrl}${separator}${decoded}`
}
